// DXF AC1009 (R12) export — proven format that opens in AutoCAD
// Uses qbiq layer names and closest ACI color per layer.

import { hasBulge } from './bulge.js'

const CRLF = '\r\n'

function tag(code, value) {
  return `${String(code).padStart(3)}${CRLF}${value}${CRLF}`
}
function flt(n) {
  if (n == null || !isFinite(n)) return '0.000000'
  return Number(n).toFixed(6)
}

// ── qbiq class → DXF layer name ───────────────────────────────────────────
// Only the layers in this list appear in the output DXF.
// Classes not listed here fall back to layer "0" via dxfLayerName().
const DXF_LAYER_NAME = {
  'Walls':                'qbiq_walls',
  'Columns':              'qbiq_columns',
  'Doors':                'qbiq_doors',
  'Windows':              'qbiq_windows',
  'Stairs':               'qbiq_stairs',
  'Railings':             'qbiq_railings',
  'Elevators':            'qbiq_elevators',
  'Mullions':             'qbiq_mullions',
  'Toilet seat':          'qbiq_toiletseat',
  'Interior Design':      'qbiq_interior_design',
  'Furniture':            'qbiq_furniture',
  'Flood':                'qbiq_flood_hatch',
  'Outdoor Flood':        'qbiq_outdoor_flood',
  'Door Lines':           'qbiq_door_lines',
  'Main Entrance Q-Line': 'qbiq_main_entrance_lines',
  'Door Q-Line':          'qbiq_door_lines',
  'Egress Q-Line':        'qbiq_egress_lines',
  // Layers below are not in the required output list — routed to layer 0
  'Core Q-Line':          '0',
  'Regular Q-Line':       '0',
  'Zones':                '0',
  'General':              '0',
  'Untagged':             '0',
}

// ── qbiq class → ACI color (closest match to target RGB) ──────────────────
// ACI: 1=red 2=yellow 3=green 4=cyan 5=blue 6=magenta 7=white 8=dk-gray
// Extended: 30=orange 34=brown 41=gold 52=cream 62=lime 72=lt-green
//           84=dk-green 93=spring-green 131=steel-blue 141=sky-blue
//           173=indigo 203=mauve 221=pink
const LAYER_ACI = {
  'Walls':                4,    // [0,255,255]   → cyan  (exact)
  'Columns':              131,  // [91,155,164]  → steel-blue
  'Doors':                84,   // [35,113,36]   → dark green
  'Windows':              13,   // [234,122,108] → salmon
  'Stairs':               221,  // [216,95,178]  → pink-magenta
  'Railings':             141,  // [0,127,255]   → sky-blue
  'Elevators':            173,  // [57,28,130]   → indigo
  'Mullions':             41,   // [250,212,20]  → gold-yellow
  'Toilet seat':          52,   // [234,213,144] → cream
  'Interior Design':      203,  // [127,63,111]  → mauve
  'Furniture':            72,   // [184,233,134] → light-green
  'Flood':                13,   // [234,134,134] → light-red
  'Outdoor Flood':        34,   // [165,125,80]  → brown
  'Door Lines':           93,   // [66,185,131]  → spring-green
  'Main Entrance Q-Line': 62,   // [203,244,148] → lime-green
  'Door Q-Line':          93,   // same as Door Lines
  'Egress Q-Line':        41,   // [238,192,69]  → gold
  'Core Q-Line':          1,    // red
  'Regular Q-Line':       8,    // gray
  'Untagged':             8,    // gray
  'Zones':                3,    // green
}

function dxfLayerName(name) {
  if (name == null || name === '' || name === '0') return '0'
  return DXF_LAYER_NAME[name] ?? '0'
}
function layerAci(name) {
  return LAYER_ACI[name] ?? 7
}

// ── Bounding box ──────────────────────────────────────────────────────────
function bbox(manifest) {
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity
  const touch=(x,y)=>{ if(!isFinite(x)||!isFinite(y))return;if(x<x0)x0=x;if(x>x1)x1=x;if(y<y0)y0=y;if(y>y1)y1=y }
  for(const e of manifest){
    if(!e)continue
    if(e.type==='LINE'){if(e.start?.length>=2)touch(e.start[0],e.start[1]);if(e.end?.length>=2)touch(e.end[0],e.end[1])}
    else if(e.type==='CIRCLE'&&e.center?.length>=2){const r=Number(e.radius)||0;touch(e.center[0]-r,e.center[1]-r);touch(e.center[0]+r,e.center[1]+r)}
    else if(e.type==='LWPOLYLINE'){for(const p of e.points??[])if(p?.length>=2)touch(p[0],p[1])}
  }
  if(!isFinite(x0)){x0=0;y0=0;x1=100;y1=100}
  return{x0,y0,x1,y1}
}

// ── Main generator ─────────────────────────────────────────────────────────
export function generateDxf(manifest, exported = []) {
  if (!manifest?.length) return null

  // Collect unique DXF layer names (multiple classes may share one DXF layer)
  const dxfLayers = new Map() // dxfName → first class name seen (for color lookup)
  dxfLayers.set('0', '0')
  for (const e of manifest) {
    if (!e?.layer) continue
    const n = dxfLayerName(e.layer)
    if (!dxfLayers.has(n)) dxfLayers.set(n, e.layer)
  }

  const { x0, y0, x1, y1 } = bbox(manifest)
  let o = ''
  let handleCounter = 0x100
  const nextHandle = () => (handleCounter++).toString(16).toUpperCase()

  // ── HEADER ────────────────────────────────────────────────────────────────
  o += tag(0,'SECTION') + tag(2,'HEADER')
  o += tag(9,'$ACADVER') + tag(1,'AC1009')
  o += tag(9,'$HANDLING') + tag(70,'1')
  o += tag(9,'$EXTMIN')  + tag(10,flt(x0)) + tag(20,flt(y0)) + tag(30,'0.000000')
  o += tag(9,'$EXTMAX')  + tag(10,flt(x1)) + tag(20,flt(y1)) + tag(30,'0.000000')
  o += tag(9,'$LIMMIN')  + tag(10,flt(x0)) + tag(20,flt(y0))
  o += tag(9,'$LIMMAX')  + tag(10,flt(x1)) + tag(20,flt(y1))
  o += tag(9,'$LTSCALE')   + tag(40,'1.000000')
  o += tag(9,'$INSUNITS')  + tag(70,'5')
  o += tag(9,'$CLAYER')    + tag(8,'0')
  o += tag(0,'ENDSEC')

  // ── TABLES ────────────────────────────────────────────────────────────────
  o += tag(0,'SECTION') + tag(2,'TABLES')

  // LTYPE
  o += tag(0,'TABLE') + tag(2,'LTYPE') + tag(70,'1')
  o += tag(0,'LTYPE') + tag(2,'CONTINUOUS') + tag(70,'0')
  o += tag(3,'Solid line') + tag(72,'65') + tag(73,'0') + tag(40,'0.000000')
  o += tag(0,'ENDTAB')

  // LAYER
  o += tag(0,'TABLE') + tag(2,'LAYER') + tag(70,String(dxfLayers.size))
  for (const [dxfName, className] of dxfLayers) {
    o += tag(0,'LAYER') + tag(2,dxfName) + tag(70,'0') + tag(62,String(layerAci(className))) + tag(6,'CONTINUOUS')
  }
  o += tag(0,'ENDTAB')

  o += tag(0,'ENDSEC')

  // ── BLOCKS (empty) ────────────────────────────────────────────────────────
  o += tag(0,'SECTION') + tag(2,'BLOCKS') + tag(0,'ENDSEC')

  // ── ENTITIES ──────────────────────────────────────────────────────────────
  o += tag(0,'SECTION') + tag(2,'ENTITIES')

  // R12 exports boundary rings, not filled HATCH entities.
  const boundaries = manifest.flatMap(e => e?.type === 'LWPOLYLINE'
    ? [e, ...(e.holes ?? []).map((points, index) => ({ type: 'LWPOLYLINE', handle: e.handle, boundary: `hole-${index}`, layer: e.layer, points, closed: true }))]
    : [e])
  for (const e of boundaries) {
    if (!e?.type) continue
    const lay = dxfLayerName(e.layer || '0')
    const entityTag = type => {
      const handle = nextHandle()
      exported.push({ entity_id: e.handle ?? null, dxf_handle: handle, dxf_layer: lay, boundary: e.boundary ?? 'exterior' })
      return tag(0, type) + tag(5, handle) + tag(8, lay)
    }

    if (e.type === 'LINE' && e.start?.length >= 2 && e.end?.length >= 2) {
      o += entityTag('LINE')
      o += tag(10,flt(e.start[0])) + tag(20,flt(e.start[1])) + tag(30,'0.000000')
      o += tag(11,flt(e.end[0]))   + tag(21,flt(e.end[1]))   + tag(31,'0.000000')

    } else if (e.type === 'CIRCLE' && e.center?.length >= 2 && e.radius > 0) {
      o += entityTag('CIRCLE')
      o += tag(10,flt(e.center[0])) + tag(20,flt(e.center[1])) + tag(30,'0.000000')
      o += tag(40,flt(e.radius))

    } else if (e.type === 'LWPOLYLINE' && e.points?.length >= 2) {
      const pts = e.points.map((point, index) => ({ point, index })).filter(({ point }) => point?.length >= 2)
      if (pts.length < 2) continue
      const flags = e.closed ? 1 : 0
      o += entityTag('POLYLINE') + tag(66,'1') + tag(70,String(flags))
      o += tag(10,'0.000000') + tag(20,'0.000000') + tag(30,'0.000000')
      const bulges = e.bulge ?? e.bulges
      pts.forEach(({ point: pt, index: i }) => {
        o += tag(0,'VERTEX') + tag(5,nextHandle()) + tag(8,lay)
        o += tag(10,flt(pt[0])) + tag(20,flt(pt[1])) + tag(30,'0.000000')
        if (hasBulge(bulges?.[i])) o += tag(42,flt(bulges[i]))
        o += tag(70,'0')
      })
      o += tag(0,'SEQEND') + tag(5,nextHandle()) + tag(8,lay)
    }
  }

  o += tag(0,'ENDSEC')
  o += tag(0,'EOF')
  return o
}

export function downloadDxf(manifest, filename) {
  const exported = []
  const content = generateDxf(manifest, exported)
  if (!content) return []
  const blob = new Blob([content], { type: 'application/dxf' })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename })
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
  return exported
}
